import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Redirect, Link } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import {faHistory} from '@fortawesome/fontawesome-free-solid/';
import {faBug} from '@fortawesome/fontawesome-free-solid/';

import CalibrationSection from "./calibration_section";
import { EntityImageGallery } from '../Entity';
import { DeviceDataOverrideService, DeviceInventoryService, DevicesService, EntityService, TimeFormattingService } from "../../services";
import { PageTitle } from '../PageTitle';
import { EntityIcon } from '../Entity';
import { FormatHelper } from '../../helpers';
import { LastReceivedRow } from '../Common';
import UserFlagsService from '../../services/user_flags_service';
import BLEService from '../../services/ble_service';

import moment from 'moment-timezone';
import i18next from "i18next";
import { FeatureAccess } from '../FeatureAccess';

import config from "config";
import Alert from 'react-bootstrap/lib/Alert';

const GATEWAY_ENTRY_METADATA_TYPES = ["gateway_id", "gateway_mac"];

const DeviceView = ({ match, location }) => {
  const [device, setDevice] = useState(null);
  const [subDevices, setSubDevices] = useState([]);
  const [controllerDevice, setControllerDevice] = useState(null);
  const [entity, setEntity] = useState(null);
  const [gateway, setGateway] = useState(null);
  const [alertConfigurations, setAlertConfigurations] = useState([]);
  const [assetMeasures, setAssetMeasures] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [loading, setLoading] = useState(true);

  const deviceId = match.params.deviceId;
  
  const loadData = useCallback(() => {
    setLoading(true);

    DevicesService.get(deviceId, true).then((response) => {
      let requests = [];

      requests.push(DevicesService.getSubDevices(response.device.serialNumber).then((response) => {
        setSubDevices(response.subDevices);
      }));

      if (response.device.controllerSerialNumber != null) {
        requests.push(DevicesService.getBySerialNumber(response.device.controllerSerialNumber).then((response) => {
          setControllerDevice(response.device);
        }));
      }

      requests.push(EntityService.getBySerialNumber(response.device.serialNumber).then((response) => {
        setEntity(response.entity);
      }));

      requests.push(DevicesService.getAssociatedAssetMeasures(deviceId).then((response) => {
        setAssetMeasures(response.assetMeasures.filter((assetMeasure) => assetMeasure.endAt == null));
      }));

      if (response.device.deviceGrouping !== null) {
       requests.push(DevicesService.getEventConfigurations(deviceId).then(({configurations}) => {
          setAlertConfigurations(configurations);
          setDevice(response.device);
        }));
      } else {
        setDevice(response.device);
      }

      const gatewayMac = response.device.deviceMetadata.find((metadata) => metadata.deviceMetadataType.name === "gateway_mac")
      if (gatewayMac?.stringValue) {
        requests.push(DevicesService.getGatewayFromMacAddress(gatewayMac.stringValue).then(({ device }) => {
            setGateway(device);
        }));
      }

      const gatewayId = response.device.deviceMetadata.find((metadata) => metadata.deviceMetadataType.name === "gateway_id")
      if (gatewayId?.stringValue) {
        requests.push(DevicesService.getAll([{search: gatewayId.stringValue}], true).then(({ devices }) => {
            if (devices?.length > 0) {
              setGateway(devices[0]);
            }
        }));
      }

      Promise.all(requests).then((_) => {
        setLoading(false);
      }).catch((error) => {
        console.error(error);
        setErrorMessage(i18next.t("messages.could-not-load-device"));
        setLoading(false);
      });
    }).catch((error) => {
      console.error(error);
      setLoading(false);
      setErrorMessage(i18next.t('messages.could-not-load-device'));
    });
  });

  useEffect(loadData, [deviceId]);

  if (errorMessage) {
    return (<Redirect to={{ pathname: '/config/devices', state: { errorMessage: errorMessage } }}/>);
  } else if (!loading) {
    return <DeviceDetail gateway={gateway}
                         device={device}
                         entity={entity}
                         refresh={loadData}
                         alertConfigurations={alertConfigurations}
                         backLinkSearch={location.state?.backLinkSearch}
                         searchValue={location.state?.searchValue}
                         assetMeasures={assetMeasures}
                         subDevices={subDevices}
                         controllerDevice={controllerDevice} 
                         successMessage={location.state?.successMessage}
            />
  } else {
    return (<div className="loader">{ i18next.t('loading') }</div>);
  }
}

const DeviceDetailInternal = ({me, device, entity, gateway, refresh, alertConfigurations, backLinkSearch, searchValue, assetMeasures, subDevices, controllerDevice, successMessage}) => {
    const [currentMetadataSelectionForHistory, setCurrentMetadataSelectionForHistory] = useState(null);
    const [showMetadataHistoryModal, setShowMetadataHistoryModal] = useState(false);
    const [hideInternalMetadata, setHideInternalMetadata] = useState(true);
    const [hideNonPrimaryData, setHideNonPrimaryData] = useState(true);
    const [amtMetadataId, setAMTMetadataId] = useState(null);
    const [nonPrimaryDataValueTypes, setNonPrimaryDataValueTypes] = useState([]);
    const [availableDataValueTypes, setAvailableDataValueTypes] = useState([]);
    const [availableMetadataValueTypes, setAvailableMetadataValueTypes] = useState([]);
    const [localSuccessMessage, setLocalSuccessMessage] = useState(successMessage);

    const clearSuccessMessage = useCallback(() => {
      setLocalSuccessMessage(null);
      window.history.replaceState({}, '');
    }, [successMessage]);

    useMemo(() => {
      const metadataTypeWithGAMT = device.deviceType.metadataTypes.find(metadataType => metadataType.groupAssociableMetadataTypes.length > 0);
      if (metadataTypeWithGAMT) {
        setAMTMetadataId(metadataTypeWithGAMT.groupAssociableMetadataTypes[0].id);
      } else {
        setAMTMetadataId(null);
      }
      setNonPrimaryDataValueTypes(device.deviceType.dataTypes.filter((datatype) => datatype.primary));
    }, [device]);

    useMemo(() => {
      setAvailableDataValueTypes(nonPrimaryDataValueTypes.length !== 0 && hideNonPrimaryData ? nonPrimaryDataValueTypes : device.deviceType.dataTypes);
    }, [nonPrimaryDataValueTypes, hideNonPrimaryData]);

    useMemo(() => {
      if (hideInternalMetadata) {
        setAvailableMetadataValueTypes(device.deviceType.metadataTypes.filter((metadataType) => metadataType.groupAssociableMetadataTypes.length === 0 && !metadataType.internal));
      } else {
        setAvailableMetadataValueTypes(device.deviceType.metadataTypes.filter((metadataType) => metadataType.groupAssociableMetadataTypes.length === 0));
      }
    }, [hideInternalMetadata]);

    const datapointSort = (datatypeA, datatypeB) => {
        if (datatypeA.primary && !datatypeB.primary) return -1;
        if (!datatypeA.primary && datatypeB.primary) return 1;
        if (datatypeA.displayOrder < datatypeB.displayOrder) return -1;
        if (datatypeA.displayOrder > datatypeB.displayOrder) return 1;
        if (datatypeA.name < datatypeB.name) return -1;
        if (datatypeA.name > datatypeB.name) return 1;
        return 0;
    };

    const metadataSort = (entryA, entryB) => {
        if (entryA.displayOrder < entryB.displayOrder) return -1;
        if (entryA.displayOrder > entryB.displayOrder) return 1;
        if (entryA.displayName < entryB.displayName) return -1;
        if (entryA.displayName > entryB.displayName) return 1;
        return 0;
    };

    const fetchCalibrationDate = (device) => {
      const calibrationDate = device.deviceMetadata.filter((dm) => dm.deviceMetadataType.name == "calibration_effective_at");
      return calibrationDate != null && calibrationDate.length > 0 ? calibrationDate[0].numericValue : null;
    };
    
    return (
        <div className="device-view devices">
            <div className="row">
                <PageTitle
                    title={device.displayName}
                    backLinkWithParams={{ link: { pathname: "/config/devices", search: backLinkSearch, state: { searchValue: searchValue } } }}
                    refreshFunction={() => { clearSuccessMessage(); refresh(); }}
                    editLinkWithParams={{
                        link: {
                            pathname: `/config/devices/edit/${device.id}`,
                            uiroute: "/config/devices/edit/:deviceId",
                            state: {
                                backLinkSearch: backLinkSearch,
                                searchValue: searchValue,
                                hideInternalMetadata: hideInternalMetadata,
                            },
                        },
                    }}
                />
            </div>

            {
              localSuccessMessage && 
              <div className="row"> 
                <div className="col-sm-12">
                  <Alert bsStyle="success" onDismiss={clearSuccessMessage}>
                    {localSuccessMessage}
                  </Alert>
                </div>
              </div>
            }

            <div className="op-content-box">
                <div>
                    <div className="row">
                        <div className="col-sm-3">
                            <div className="row">
                                <div className="col-sm-12 form-group">
                                    <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-category">
                                        <label>{i18next.t("form-labels.device-type")}</label>
                                        <p>{i18next.t(`device-types.${device.deviceType.token}`)} {device.deviceType.token == 'twrxnodev1' && device.serialNumber && <NodeScanAction serialNumber={device.serialNumber} />}</p>
                                    </div>
                                    <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-status">
                                        <label>Status:</label>
                                        <p>
                                            <EntityIcon width={"16"} height={"16"} entity={entity} /> {i18next.t(`device-statuses.${device.deviceStatus.token}`)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-3">
                            <div className="row">
                                <div className="col-sm-12 form-group">
                                    <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-group">
                                        <label>{i18next.t("form-labels.group")}</label>
                                        {device.deviceGrouping?.name ?? i18next.t("messages.no-grouping")}
                                    </div>
                                    <div className="card-data-cell remove-border-left card-data-cell-icon card-icon-status">
                                        <label>{i18next.t("form-labels.serial-number")}</label>
                                        <p style={{ wordWrap: "break-word" }}>{device.serialNumber}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="col-sm-6">
                            <div className="card-data-side-info">
                                <div className="row">
                                    <div className="col-sm-8 col-md-6 form-group">
                                        <div className="card-data-cell remove-border-left">
                                            <label>{i18next.t("form-labels.first-detected-at")}</label>
                                            <p>{TimeFormattingService.renderNormalTime(device.firstDetectedAt, device.deviceGrouping?.timezone)}</p>
                                        </div>
                                    </div>
                                    <div className="col-sm-8 col-md-6 form-group">
                                        <div className="card-data-cell remove-border-left">
                                            <label>{i18next.t("form-labels.last-device-time")}</label>
                                            <p>{TimeFormattingService.renderNormalTime(device.lastDeviceTime, device.deviceGrouping?.timezone)}</p>
                                        </div>
                                    </div>
                                    <div className="col-sm-8 col-md-6">
                                        <div className="card-data-cell remove-border-left">
                                            <label>{i18next.t("form-labels.last-updated")}</label>
                                            <p>{TimeFormattingService.renderFromNowTime(device.lastUpdatedAt, device.deviceGrouping?.timezone)}</p>
                                        </div>
                                    </div>
                                    {
                                      device.calibrationImages?.length > 0 && 
                                        <CalibrationSection 
                                          imageSrc={`${config.API_BASE_URL}entities/${device.serialNumber}/images/${device.calibrationImages[0].name}`} 
                                          calibrationDate={fetchCalibrationDate(device)}
                                          timezone={device.deviceGrouping?.timezone}
                                          inSidebar={false}
                                        />
                                    }
                                    {device.controllerSerialNumber && controllerDevice && (
                                        <div className="col-sm-8 col-md-6">
                                            <div className="card-data-cell remove-border-left">
                                                <label>{i18next.t("form-labels.controlled-by")}</label>
                                                <p> <Link to={`/config/devices/view/${controllerDevice.id}`}>{controllerDevice.displayName}</Link></p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        {availableDataValueTypes?.length > 0 && (
                            <div className="panel panel-default op-panel device-table device-last-received-data">
                                <div className="panel-heading">
                                    {i18next.t("pages.device-configuration.last-received-data")}
                                    <span className="inline pull-right">
                                        {nonPrimaryDataValueTypes.length > 0 && (
                                            <label>
                                                <input type="checkbox" className="inline" checked={hideNonPrimaryData} onChange={() => setHideNonPrimaryData(!hideNonPrimaryData)} />
                                                {i18next.t("form-labels.hide-non-primary")}
                                            </label>
                                        )}
                                    </span>
                                </div>
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>{i18next.t("table-headers.name")}</th>
                                            <th>{i18next.t("table-headers.current-value")}</th>
                                            <th>{i18next.t("table-headers.last-updated")}</th>
                                            <FeatureAccess feature="ENABLE_THRESHOLD_CLASSIFICATION_MATCHING">
                                                <th>Exclusions</th>
                                            </FeatureAccess>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {availableDataValueTypes.sort(datapointSort).map((dataType) => (
                                            <LastReceivedRow
                                                key={dataType.id}
                                                dataType={dataType}
                                                thresholdsExceeded={device.thresholdsExceeded}
                                                deviceName={device.displayName}
                                                deviceLastReceivedData={device.deviceLastReceivedData}
                                                device={device}
                                                deviceId={device.id}
                                                timezone={device.deviceGrouping?.timezone}
                                                deviceType={device.deviceType}
                                            />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        <div className="panel panel-default device-table op-panel">
                            <div className="panel-heading">
                                {i18next.t("pages.device-configuration.device-metadata")}
                                {me.userType.token === "internal" && (
                                    <span className="inline pull-right">
                                        <label>
                                            <input
                                                type="checkbox"
                                                className="inline"
                                                checked={hideInternalMetadata}
                                                onChange={() => {
                                                    if (me.userType.token === "internal") {
                                                        setHideInternalMetadata(!hideInternalMetadata);
                                                    }
                                                }}
                                            />
                                            {i18next.t("form-labels.hide-internal")}
                                        </label>
                                    </span>
                                )}
                            </div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{i18next.t("table-headers.name")}</th>
                                        <th>{i18next.t("table-headers.current-value")}</th>
                                        <th>{i18next.t("table-headers.direction")}</th>
                                        <th>{i18next.t("table-headers.last-sync-status")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {availableMetadataValueTypes.sort(metadataSort).map((metadataType) => (
                                        <MetadataRow
                                            key={metadataType.id}
                                            gateway={gateway}
                                            metadataType={metadataType}
                                            metadata={device.deviceMetadata}
                                            timezone={device.deviceGrouping?.timezone}
                                            displayMetadataHistoryModal={(metadata) => {
                                                setCurrentMetadataSelectionForHistory(metadata);
                                                setShowMetadataHistoryModal(true);
                                            }}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="panel panel-default device-table op-panel">
                            <div className="panel-heading">{i18next.t("pages.device-configuration.tags")}</div>
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        <th>{i18next.t("table-headers.name")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {device.tags.length === 0
                                        ? [
                                              <tr key={1}>
                                                  {" "}
                                                  <td>{i18next.t("messages.no-tags")}</td>{" "}
                                              </tr>,
                                          ]
                                        : device.tags.map((tag) => <DeviceTagRow key={tag.id} tag={tag} />)}
                                </tbody>
                            </table>
                        </div>
                        {subDevices.length > 0 && (
                            <div className="panel panel-default device-table op-panel">
                                <div className="panel-heading">{i18next.t("pages.device-configuration.sub-devices")}</div>
                                <table className="table table-striped">
                                    <thead>
                                        <tr>
                                            <th>{i18next.t("table-headers.name")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subDevices.map((subDevice) => (
                                            <tr key={subDevice.id}>
                                                <td>
                                                    <Link to={`/config/devices/view/${subDevice.id}`}>{subDevice.displayName}</Link>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="panel panel-default device-table op-panel">
                            <div className="panel-heading">{i18next.t("pages.device-configuration.applicable-events-and-thresholds")}</div>
                            {alertConfigurations.length === 0 ? (
                                <div style={{ paddingBottom: 5 }}>{i18next.t("messages.no-events-or-thresholds")}</div>
                            ) : (
                                alertConfigurations.map((config) => (
                                    <AlertConfigRow
                                        key={config.id}
                                        config={config}
                                        data={device.deviceLastReceivedData}
                                        thresholdsExceeded={device.thresholdsExceeded}
                                        thresholdDeviceDataTypeExclusions={device.thresholdDeviceDataTypeExclusions}
                                    />
                                ))
                            )}
                        </div>
                        {
                          assetMeasures && assetMeasures.length > 0 && 
                          <div className="panel panel-default device-table op-panel">
                            <div className="panel-heading">{i18next.t("pages.device-configuration.associated-assets")}</div>
                            <AssetMeasureRow assetMeasures={assetMeasures} />
                          </div>
                        }
                        <div className="row grey-box">
                            <div className="col-sm-6 oc-mini-card oc-mini-card">
                                <div className="panel-heading">{i18next.t("pages.device-configuration.floor-plan-location")}</div>
                                <div className="inner metadata-inner form-control floor-plan">
                                    <img className="floor-plan-thumbnail" src={`${config.WEB_BASE_URL}/images/floor_plan.svg`} />
                                    <div>{amtMetadataId && <DeviceLocationViewButton deviceId={device.id} groupAssociableMetadataTypeId={amtMetadataId} />}</div>
                                </div>
                            </div>
                            <div className="col-sm-6 oc-mini-card oc-mini-card">
                                <div className="panel-heading">{i18next.t("pages.device-configuration.device-image")}</div>
                                <div className="inner metadata-inner form-control">
                                    <EntityImageGallery serialNumber={device.serialNumber} images={device.deviceImages ?? []} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showMetadataHistoryModal} onHide={() => setShowMetadataHistoryModal(!showMetadataHistoryModal)}>
                <Modal.Header closeButton>
                    <Modal.Title>{i18next.t("pages.device-configuration.metadata-history")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <MetadaHistoryTable device={device} metadata={currentMetadataSelectionForHistory} />
                </Modal.Body>
            </Modal>
        </div>
    );
};

const NodeScanAction = ({serialNumber}) => {
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [bleContext, setBLEContext] = useState(null);
  const [currentScanStatus, setCurrentScanStatus] = useState("Waiting");
  const [snMatch, setSNMatch] = useState(false);
  const [wifiScanResults, setWifiScanResults] = useState([]);
  const [referenceId, setReferenceId] = useState(null);
  const [applying, setApplying] = useState(false);
  const [caCertStatus, setCACertStatus] = useState(false);
  const [clientCertStatus, setClientCertStatus] = useState(false);
  const [clientKeyStatus, setClientKeyStatus] = useState(false);
  const [wifiStatus, setWifiStatus] = useState(false);
  const [opsenseConnectionStatus, setOpsenseConnectionStatus] = useState(false);
  
  const handleStateChanges = (name, state, changeType) => {
      if (changeType == 'notify') {
          console.log(`notify ${name}=${state[name]}`);
          switch(name) {
              case 'mqtt_ca_cert': {
                  setCACertStatus(state[name] == "VALID");
              } break;
              case 'mqtt_client_cert': {
                  setClientCertStatus(state[name] == "VALID");
              } break;
              case 'mqtt_client_key': {
                  setClientKeyStatus(state[name] == 'VALID');
              } break;
              case 'wifi_status': {
                  setWifiStatus(state[name]);
              } break;
              case 'wifi_error': {
                  if (state[name] != 0) {
                      console.error(`WiFi error ${state[name]}`);
                  }
              } break;
              case 'mqtt_status': {
                  setOpsenseConnectionStatus(state[name]);
              } break;
              case 'mqtt_error': {
                  if (state[name] != 0) {
                      console.error(`MQTT error ${state[name]}`);
                  }
              } break;
          }
      }
  };

  const doScan = useCallback(() => {
    setShowNodeModal(true);

    BLEService.startScan((message) => setCurrentScanStatus(message),  handleStateChanges, () => {
        if (bleContext.isConnected()) {
            bleContext.cleanup();
        }

        setApplying(false);
        setCACertStatus(false);
        setClientCertStatus(false);
        setClientKeyStatus(false);
        setWifiStatus(false);
        setOpsenseConnectionStatus(false);
        setBLEContext(null);
      })
      .then(({ bleContext }) => {
          setSNMatch(serialNumber == `twrxnodev1.device.${bleContext.state["device_id"]}`);
          setReferenceId(`twrxnodev1.${bleContext.state["device_id"]}`)
          setBLEContext(bleContext);
          setCurrentScanStatus("Complete");
      })
      .catch((err) => {
          console.error(err);
      });
  }, serialNumber);

  const doFactoryReset = useCallback(() => {
    BLEService.sendFactoryReset(bleContext)
      .then(() => {
        setBLEContext(null);
        setCurrentScanStatus("Reset complete");
      })
      .catch((err) => {
        console.error(err);
        setCurrentScanStatus("Reset error");
      });
  });

  const doWiFiScan = useCallback(() => {
    setWifiScanResults([]);
    
    BLEService.sendWiFiScan(bleContext, (scanResult) => { setWifiScanResults((prev) => [...prev, scanResult]); })
      .then(() => {
        setCurrentScanStatus("Scanning");
      })
      .catch((err) => {
        console.error(err);
        setCurrentScanStatus("Scan error");
      });
  });

  const reConfigure = useCallback(() => {
    setCurrentScanStatus("Reconfiguration started, fetching device configuration data");

    DeviceInventoryService.getInventoryData(referenceId)
      .then(({ inventoryData }) => {
        // console.log(inventoryData);
        setCurrentScanStatus("Device configuration started");
        setApplying(true);

        bleContext.writeValues = {
            wifi_ssid: inventoryData.extendedConfigurationData["wifi_ssid"] ?? ssid,
            wifi_password: inventoryData.extendedConfigurationData["wifi_password"] ?? wifiPassword,
            mqtt_broker_url: config.MQTT_URL,
            mqtt_broker_port: "8883",
            mqtt_ca_cert: inventoryData.extendedConfigurationData["ca_crt"],
            mqtt_client_cert: inventoryData.extendedConfigurationData["mqtt_crt"],
            mqtt_client_key: inventoryData.extendedConfigurationData["mqtt_key"],
        };

        BLEService.applyConfiguration(bleContext)
                  .then(() => {
                      console.log("configuration complete");
                      setCurrentScanStatus("Device reconfiguration complete");
                      
                      setApplying(false);  
                      setCACertStatus(false);
                      setClientCertStatus(false);
                      setClientKeyStatus(false);
                      setWifiStatus(false);
                      setOpsenseConnectionStatus(false);

                      doScan();
                  })
                  .catch((err) => {
                      console.error(err);
                      setCurrentScanStatus(`Error applying device configuration: ${err}`);

                      setApplying(false);

                      if (bleContext.isConnected()) {
                          //console.log("Running context cleanup.")
                          bleContext.cleanup();
                      }
                  });
      })
      .catch((err) => {
        console.log(err);
      });
  });

  const cleanup = useCallback(() => {
    if (bleContext) {
      bleContext.cleanup();
    }
    setBLEContext(null); 
    setCurrentScanStatus("Waiting"); 
    setWifiScanResults([]);
  });

  return UserFlagsService.isFlagEnabled('showNodeInfoScanner') ? (
    <span>
      <FontAwesomeIcon size={'sm'} icon={faBug} onClick={doScan} />

      <Modal show={showNodeModal} onHide={() => {setShowNodeModal(!showNodeModal); cleanup();}}>
          <Modal.Header closeButton>
              <Modal.Title>Node Debug</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              Scan Status: {currentScanStatus} <br/>

              {
                bleContext &&
                <div>
                  <br/>
                  {
                    !applying &&
                    <div>
                      Device Information:
                      <ul>
                        <li>Device ID: <b>{bleContext.state["device_id"]} ({snMatch ? 'Device Match' : 'Device Mismatch'})</b></li>
                        <li>Firmware Version: <b>{bleContext.state["firmware"]}</b></li>
                        <li>MQTT Broker URL: <b>{bleContext.state["mqtt_broker_url"]}</b></li>
                        <li>MQTT Broker Port: <b>{bleContext.state["mqtt_broker_port"]}</b></li>
                        <li>MQTT Username: <b>{bleContext.state["mqtt_username"]}</b></li>
                        <li>MQTT Connection Status: <b>{bleContext.state["mqtt_status"] ? "Connected" : "Disconnected"}</b></li>
                        <li>MQTT Error Code: <b>{bleContext.state["mqtt_error"]}</b></li>
                        <li>WiFi SSID: <b>{bleContext.state["wifi_ssid"]}</b></li>
                        <li>WiFi Connection Status: <b>{bleContext.state["wifi_status"] ? "Connected" : "Disconnected"}</b></li>
                        <li>WiFi Error Code: <b>{bleContext.state["wifi_error"]}</b></li>
                        <li>Hardware Version: <b>{bleContext.state["hardware"]}</b></li>
                        <li>Software Version: <b>{bleContext.state["software"]}</b></li>
                        <li>Manufacturer: <b>{bleContext.state["manufacturer"]}</b></li>
                        <li>Model: <b>{bleContext.state["model"]}</b></li>
                      </ul>
                    </div>
                  }

                  {
                    applying && 
                    <div style={{paddingTop:10}}>
                        Security Configuration: {caCertStatus && clientCertStatus && clientKeyStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                        WiFi Connection: {wifiStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                        OpSense Connection: {opsenseConnectionStatus ? <b>Ready</b> : <span>Waiting</span>} <br/>
                    </div>
                  }

                  <br/>

                  <button onClick={doWiFiScan}>WiFi Scan</button>

                  &nbsp; &nbsp; &nbsp; &nbsp;

                  <button onClick={doFactoryReset}>Factory Reset</button>

                  &nbsp; &nbsp; &nbsp; &nbsp;

                  <button onClick={reConfigure}>Reconfigure Device</button>

                  <div>
                    {
                      wifiScanResults && wifiScanResults.length > 0 &&
                      <div>
                        Scan Results:
                        <ul>
                          {wifiScanResults.map((result, i) => <li key={i}>{result}</li>)}
                        </ul>
                      </div>
                    }
                  </div>
                </div>
              }
          </Modal.Body>
      </Modal>
    </span>
  ) : (<span></span>);
};

const DeviceDetail = connect(({user}) => ({ me: user.me }))(DeviceDetailInternal);

const MetadaHistoryTable = ({device, metadata}) => {
  const [metadataHistory, setMetadataHistory] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setMetadataHistory(null);
    setErrorMessage(null);

    if (device && metadata) {
      setIsLoading(true);

      DevicesService.getDeviceMetadataHistory(device.id, metadata.id).then((response) => {
        setMetadataHistory(response.history);
        setIsLoading(false);
      }).catch((error) => {
        console.error(error);
        setErrorMessage('Error loading metadata history.');
        setIsLoading(false);
      });
    }
  }, [device, metadata]);

  const findDisplayValue = (historyEntry) => {
    if (historyEntry.stringValue) {
      return historyEntry.stringValue;
    } else if (historyEntry.numericValue) {
      return historyEntry.numericValue;
    } else if (historyEntry.complexValue) {
      return "Complex Value Changed";
    } else {
      return historyEntry.booleanValue ? "On" : "Off";
    }
  };

  return (
    isLoading ? <div className='loader'>Loading</div> :
    <div>
      { errorMessage && <div>{errorMessage}</div> }
      { 
        metadataHistory && metadataHistory.length > 0 ? 
        <table className='table table-striped'>
          <thead>
            <tr>
              <th>Previous Value</th>
              <th>Changed At</th>
              <th>Changed By</th>
            </tr>
          </thead>
          <tbody>
            <tr><td colSpan={3}>Only the previous 10 entries are displayed.</td></tr>
            {
              metadataHistory.map(historyEntry => 
                <tr key={historyEntry.id}>
                  <td>{findDisplayValue(historyEntry)}</td>
                  <td>{TimeFormattingService.renderNormalTime(device.deviceGrouping && device.deviceGrouping.timezone ? moment(historyEntry.changedAt).tz(device.deviceGrouping.timezone) : moment(historyEntry.changedAt))}</td>
                  <td>{historyEntry.changedBy ? `${historyEntry.changedBy.firstName} ${historyEntry.changedBy.lastName}` : "System"}</td>
                </tr>
              )
            }
          </tbody>
        </table>
        :
        <div>No history found</div>
      }
    </div>
  );
};

const MetadataRow = ({metadataType, metadata, gateway, displayMetadataHistoryModal, timezone}) => {
  const [currentSyncValue, setCurrentSyncValue] = useState(i18next.t('unknown'));
  const [currentValue, setCurrentValue] = useState(null);
  const [displayValue, setDisplayValue] = useState(null);
  const [historyAvailable, setHistoryAvailable] = useState(false);
  const [isGatewayEntry, setIsGatewayEntry] = useState(false);

  const getRestrictedSelection = useCallback((value) => {
    const restrictedSelection = value.stringValue ?
          metadataType.deviceMetadataRestrictedSelections.find(selection => selection.stringValue === value.stringValue) : 
          metadataType.deviceMetadataRestrictedSelections.find(selection => selection.numericValue === value.numericValue);
    return restrictedSelection ? restrictedSelection.displayValue : i18next.t('unknown');
  }, [metadataType]);

  const getDisplayValue = useCallback((metadata) => {
    var defaultValue = i18next.t('unknown');

    if (metadataType.name == 'calibration_effective_at' && metadata != null && metadata.numericValue != null) {
      return TimeFormattingService.renderNormalTime(metadata.numericValue, timezone);
    }

    switch (metadataType.deviceMetadataValueType.name) {
      case 'string': 
        defaultValue = metadataType.defaultStringValue ? metadataType.defaultStringValue :  defaultValue;
        return metadata != null && metadata.stringValue != null ? metadata.stringValue : defaultValue;
      case 'numeric':
        defaultValue = metadataType.defaultNumericValue ? metadataType.defaultNumericValue :  defaultValue;
        return metadata != null && metadata.numericValue != null ? metadata.numericValue : defaultValue;
      case 'boolean':
        defaultValue = metadataType.defaultBooleanValue != null ? metadataType.defaultBooleanValue ? 'On' : 'Off' : defaultValue;
        return metadata != null && metadata.booleanValue != null ? metadata.booleanValue ? 'On' : 'Off' : defaultValue;
      default: 
        return defaultValue;
    }
  }, [metadataType, metadata]);

  useMemo(() => {
    const isRestrictedMetadataType = metadataType.deviceMetadataRestrictedSelections.length > 0;
    const currentValue = metadata.find(metadata => metadata.deviceMetadataType.id === metadataType.id);

    setCurrentValue(currentValue);
    setHistoryAvailable(currentValue != null && currentValue.deviceMetadataType.addToHistory);
    
    if (currentValue != null && GATEWAY_ENTRY_METADATA_TYPES.includes(currentValue.deviceMetadataType.name) && gateway) {
        setIsGatewayEntry(true);
    } else {
        setIsGatewayEntry(false);
    }

    let value = currentValue != null && isRestrictedMetadataType ? getRestrictedSelection(currentValue) : getDisplayValue(currentValue);

    if (currentValue != null && currentValue.deviceMetadataType.syncDirection) {
      if (currentValue.deviceMetadataType.syncDirection.none) {
        setCurrentSyncValue(i18next.t('na'));
      } else if (currentValue.deviceMetadataType.syncDirection.up && currentValue.lastSyncTime) {
        setCurrentSyncValue(TimeFormattingService.renderNormalTime(currentValue.lastSyncTime, timezone));
      } else if (currentValue.deviceMetadataType.syncDirection.down && currentValue.lastDeviceMetadataUpdate) {
        const updateValue = isRestrictedMetadataType ? getRestrictedSelection(currentValue.lastDeviceMetadataUpdate) : getDisplayValue(currentValue.lastDeviceMetadataUpdate);
        let updateTime = null;
        if (currentValue.lastDeviceMetadataUpdate.deviceMetadataUpdateStatus.token === 'pending') {
          if (currentValue.lastDeviceMetadataUpdate.startedAt) {
              updateTime = TimeFormattingService.renderNormalTime(currentValue.lastDeviceMetadataUpdate.startedAt, timezone);
          }
        } else {
          updateTime = TimeFormattingService.renderNormalTime(currentValue.lastDeviceMetadataUpdate.endedAt, timezone);
        }
        let changedBy = "";
        if (currentValue.lastDeviceMetadataUpdate.changedBy != null) {
          changedBy = <span><br/> {i18next.t('changed-by')} {currentValue.lastDeviceMetadataUpdate.changedBy.firstName} {currentValue.lastDeviceMetadataUpdate.changedBy.lastName}</span>;
        }
        setCurrentSyncValue(<span>{currentValue.lastDeviceMetadataUpdate.deviceMetadataUpdateStatus.name} ({updateValue}) {i18next.t('as-of')} {updateTime === null ? i18next.t('na') : updateTime} {changedBy}</span>);
      }
    }

    setDisplayValue(value);
  }, [metadataType, metadata]);

  return (
    <tr key={metadataType.id}>
      <td>{ i18next.t(`device-metadata-types.${metadataType.name}`) }</td>
      { 
        isGatewayEntry ? 
          <td>{displayValue} {historyAvailable ? <FontAwesomeIcon size={'sm'} icon={faHistory} onClick={() => displayMetadataHistoryModal(currentValue)} /> : null} (<Link to={`/config/devices/view/${gateway.id}`}> {gateway.displayName} </Link>)</td> : 
          <td>{displayValue} {historyAvailable ? <FontAwesomeIcon size={'sm'} icon={faHistory} onClick={() => displayMetadataHistoryModal(currentValue)} /> : null}</td> 
      }
      <td className={metadataType.syncDirection.name}>{ i18next.t(`sync-directions.${metadataType.syncDirection.id}`) }</td>
      <td>{currentSyncValue}</td>
    </tr>
  );
};

const DeviceLocationViewButton = ({deviceId, groupAssociableMetadataTypeId}) => (
  <Link className="btn btn-xs btn-default" to={`/config/devices/${deviceId}/amtview/${groupAssociableMetadataTypeId}`}>
    { i18next.t('pages.device-configuration.view-device-location') }
  </Link>
);

const DeviceTagRow = ({tag}) => (
  <tr key={tag.id}>
    <td>{tag.name}</td>
  </tr>
);

const AlertConfigRow = ({config, data, thresholdsExceeded, thresholdDeviceDataTypeExclusions}) => {
  const [linkTo, setLinkTo] = useState(null);
  const unique = a => [...new Set(a.map(o => JSON.stringify(o)))].map(s => JSON.parse(s))
  const associatedThresholds = unique(config.alertConfigurationActions.map((action) => action.threshold));

  if (linkTo != null) {
    return <Redirect to={{ pathname: linkTo }} />;
  }

  return (
    <div key={config.id} className="device-table" style={{paddingTop: '5px'}}>
      <table className="table table-striped">
        <thead>
          <tr key={config.id}>
            <th onClick = {() => { setLinkTo(`/config/events/view/${config.id}`); }}>Alert: {config.name}</th>
          </tr>
        </thead>
        <tbody key={config.id}>
          {
            associatedThresholds.length === 0 ? 
              <tr key={1}><td>{ i18next.t('messages.no-thresholds') }</td></tr> : 
              associatedThresholds.map(threshold => <DeviceThresholdRow key={threshold.id} threshold={threshold} data={data} thresholdsExceeded={thresholdsExceeded} thresholdDeviceDataTypeExclusions={thresholdDeviceDataTypeExclusions} /> )
          }
        </tbody>
      </table>
    </div>
  );
}

const AssetMeasureRow = ({assetMeasures}) => (
    <div className="device-table" style={{paddingTop: '5px'}}>
      <table className="table table-striped">
        <thead>
          <tr>
            <th>Asset Name</th>
            <th>Device Data</th>
            <th>Asset Measure Name</th>
          </tr>
        </thead>
        <tbody>
          {
              assetMeasures.map((assetMeasure) => {
                return (
                  <tr key={assetMeasure.id}>
                    <td>{assetMeasure.asset.name}</td>
                    <td>{assetMeasure.deviceDataType.name}</td>
                    <td>{assetMeasure.assetTypeDeviceDataType.name}</td>
                  </tr>
                )})
          }
        </tbody>
      </table>
    </div>
);

const DeviceThresholdRow = ({threshold, data, thresholdsExceeded, thresholdDeviceDataTypeExclusions}) => {
  const measurementValue = FormatHelper.formatMeasurement(threshold);
  return data
  .map((datapoint) => {
    if (datapoint.numericValue != null) {
        datapoint = { ...datapoint, numericValue: DeviceDataOverrideService.getOverrideFunctionForDeviceTypeToken(datapoint.deviceDataType.token)(datapoint.numericValue) };
    }
    const deviceDataTypeMatches = datapoint.deviceDataType && threshold.deviceDataType && datapoint.deviceDataType.token === threshold.deviceDataType.token;
    const deviceDataClassificationMatches = datapoint.deviceDataType?.deviceDataClassification && threshold.deviceDataClassification && datapoint.deviceDataType.deviceDataClassification.token === threshold.deviceDataClassification.token;
    if (deviceDataTypeMatches || deviceDataClassificationMatches) {

      var excluded = false;
      if (deviceDataClassificationMatches) {
        excluded = thresholdDeviceDataTypeExclusions.find((exclusion) => exclusion.deviceDataType.id === datapoint.deviceDataType.id) != null;
      }

      var dataTypeToken = datapoint.deviceDataType.token;
      if (thresholdsExceeded) {
        const foundThresholdExceeded = thresholdsExceeded.filter(thresholdExceeded => { 
          if (thresholdExceeded.deviceDataClassification) {
            return thresholdExceeded.deviceDataClassification.id === datapoint.deviceDataType.deviceDataClassification.id;
          } else {
            return thresholdExceeded.deviceDataType.id === datapoint.deviceDataType.id;
          }
        });
        if (foundThresholdExceeded && foundThresholdExceeded.length > 0) {
          dataTypeToken = foundThresholdExceeded[0].thresholdClassification.token;
        }
      }

      return (
        <tr key={`${threshold.id}-${datapoint.deviceDataType.id}`}>
          <td>
            { excluded && <b>Excluded&nbsp;</b> }
            { i18next.t('pages.device-configuration.threshold') } {threshold.name}&nbsp;
            <span>              
              <img src={`${config.WEB_BASE_URL}/images/icon/sensor_${dataTypeToken}.svg`} height="16" /> Current evaluation of {datapoint.deviceDataType.name}: {FormatHelper.formatMeasurement(threshold, datapoint)}&nbsp;
              <span className="threshold-evaluation">{threshold.thresholdEvaluationType.shortForm}</span>&nbsp;
              {measurementValue}
            </span>
          </td>        
        </tr>
      );

    }
  })
  .filter(entry => entry); // Remove null entries
}

export default DeviceView;
